import { entranceAnimations } from './entrance-animations';
import { loopAnimations } from './loop-animations';
import { hoverAnimations } from './hover-animations';
import { clickAnimations } from './click-animations';
import { scrollAnimations } from './scroll-animations';
import { mouseAnimations } from './mouse-animations';
import { AnimationCategory, AnimationDynamicSettings, AnimationType } from './animation/animations.model';

/**
 * Main animation configuration object
 * Combines all animation categories into a single exportable configuration
 */
export const animationConfigs: Record<
  AnimationCategory,
  Partial<
    Record<AnimationType, (s: AnimationDynamicSettings, el: Element, opts: gsap.TweenVars) => gsap.core.Tween | null>
  >
> = {
  entrance: entranceAnimations,
  loop: loopAnimations,
  hover: hoverAnimations,
  click: clickAnimations,
  scroll: scrollAnimations,
  mouse: mouseAnimations,
};

/**
 * Apply animation to an element
 * @param category - Animation category
 * @param type - Animation type
 * @param settings - Dynamic settings for the animation
 * @param element - Target element
 * @param options - Additional GSAP options
 * @returns The created tween or null if animation not found
 */
export function applyAnimation(
  category: AnimationCategory,
  type: AnimationType,
  settings: AnimationDynamicSettings,
  element: Element,
  options: gsap.TweenVars = {}
): gsap.core.Tween | null {
  const categoryAnimations = animationConfigs[category];
  if (!categoryAnimations) {
    console.warn(`Animation category '${category}' not found`);
    return null;
  }

  const animationFunction = categoryAnimations[type];
  if (!animationFunction) {
    console.warn(`Animation type '${type}' not found in category '${category}'`);
    return null;
  }

  try {
    return animationFunction(settings, element, options);
  } catch (error) {
    console.error(`Error applying animation ${category}.${type}:`, error);
    return null;
  }
}

/**
 * Check if an animation exists
 * @param category - Animation category
 * @param type - Animation type
 * @returns true if animation exists
 */
export function hasAnimation(category: AnimationCategory, type: AnimationType): boolean {
  return !!animationConfigs[category]?.[type];
}

/**
 * Get all available animations for a category
 * @param category - Animation category
 * @returns Array of available animation types
 */
export function getAvailableAnimations(category: AnimationCategory): AnimationType[] {
  const categoryAnimations = animationConfigs[category];
  if (!categoryAnimations) return [];

  return Object.keys(categoryAnimations) as AnimationType[];
}

/**
 * Get all available categories
 * @returns Array of available animation categories
 */
export function getAvailableCategories(): AnimationCategory[] {
  return Object.keys(animationConfigs) as AnimationCategory[];
}

/**
 * Validate animation configuration
 * @param category - Animation category
 * @param type - Animation type
 * @param settings - Animation settings
 * @returns Validation result with errors if any
 */
export function validateAnimation(
  category: AnimationCategory,
  type: AnimationType,
  settings: AnimationDynamicSettings
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!hasAnimation(category, type)) {
    errors.push(`Animation '${type}' not found in category '${category}'`);
  }

  // Add specific validation rules based on animation type
  if (type.startsWith('scroll-') && !settings.start && !settings.end) {
    errors.push('Scroll animations should have start and/or end triggers defined');
  }

  if (type.includes('spin') && settings.spin_count && settings.spin_count < 0) {
    errors.push('Spin count should be a positive number');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
